import { IAction, IObjectSignature } from 'types';
import { select, all, call, spawn } from 'typed-redux-saga';
import {
    getCachedFullContainers,
    getOmjsContainer,
    getOmjsLayerConfig, getOmjsViewInstance, getOperationData
} from 'selectors';
import { OmjsService } from 'services/omjs/omjs-service';
import { MapService } from 'services/map/map-service';
import { ObjectsHelper } from 'utils';
import {
    Service,
    bindActionsTyped,
    leading,
    latest
} from '../service-utils';
import { operation, OperationsService } from '../operations/operations-service';
import { ContainersService } from '../containers/containers-service';
import { routes } from '../../routes';

export interface IShowObjectWindowOperationData {
    layer_code: string;
    object: omjs.IObjectJSON;
    structure: omjs.ILayerColumnJSON[];
    filtered_structure: omjs.ILayerColumnJSON[];
}

class ObjectWindowService extends Service {
    static serviceName = 'ObjectWindowService';
    static generatedStopActionTypes: string[] = [];
    static generatedWatchers: Function[] = [];

    @leading
    @operation('SHOW_OBJECT_WINDOW')
    static* showObjectWindow(action: IAction<IObjectSignature>) {
        let { layer_code, object_id } = action.payload;
        let view = yield* select(getOmjsViewInstance);
        let layer_config = yield* select(getOmjsLayerConfig, layer_code);

        const fullContainers = yield* select(getCachedFullContainers);
        const targetContainer = fullContainers.find(c => c.get('code') === layer_code);
        if (!layer_config || !layer_config.balloonEnabled) {
            // TODO:
            routes.redirect(routes.map, { container: 'root' });
            // return null;
            throw new Error();
        }

        // @ts-ignore
        const [ object, structure ]: [
            omjs.IObjectJSON,
            omjs.ILayerColumnJSON[]
        ] = yield* all([
            call(OmjsService.loadProjectObject, targetContainer || layer_code, object_id, {
                raw: true,
                returnGeom: 1, // с геометрией
                geomZoom: Math.round(view.getMap().getView().getZoom()),
                geomSR: '3857', // с нужной проекцией
                fields: '*', // все поля паком
                f: 'fjson'
            }),
            call(OmjsService.loadProjectLayerStructure, targetContainer || layer_code)
        ]);

        const bounds = targetContainer.get('json')?.bounds;
        if (bounds) {
            const f = new omjs.ol.Feature({});
            f.geometry = new omjs.ol.geom.GeometryCollection([]);
            const filterObjects = yield* call(OmjsService.loadLayerObjects, bounds.layer,
                {
                    returnGeom: 1,
                    geomSR: 3857,
                    fcode: bounds.column,
                    fval: object.id,
                    fields: bounds.column
                }) as any;

            const fullGeomList = filterObjects.getLength() > 0
                ? filterObjects.getArray().map((o) => {
                    const geom = o.getGeometry();
                    return new omjs.ol.geom[geom.type](geom.coordinates);
                })
                : null;

            if (fullGeomList) {
                f.geometry.setGeometries(fullGeomList);
                const format = new omjs.ol.format.GeoJSON();
                const fullGeom = JSON.parse(format.writeGeometry(f.geometry));
                object.fullGeom = fullGeom;
            } else {
                object.fullGeom = null;
            }
        }
        yield call(ContainersService.setContainerSelected, {
            type: 'setContainerSelected',
            payload: { id_or_code: layer_code, selected: true }
        });

        return {
            layer_code,
            object,
            structure,
            filtered_structure: ObjectsHelper.filterStructure(
                structure,
                layer_config,
                object,
            )
        } as IShowObjectWindowOperationData;
    }

    @latest
    static* setHighlightGeometry() {
        let objectInfo = yield select(state => getOperationData(state, 'SHOW_OBJECT_WINDOW'));
        if (!objectInfo) return;
        const view = yield select(getOmjsViewInstance);
        const object = yield call(OmjsService.loadProjectObject, objectInfo.layer_code, objectInfo.object.id, {
            returnGeom: 1, // с геометрией
            geomZoom: Math.round(view.getMap().getView().getZoom()),
            geomSR: '3857' // с нужной проекцией
        });
        yield call(MapService.setObjectHighlight, { type: 'setSysLayer',
            payload: object.getGeometry() });
    }
}


export const objectWindowService: ObjectWindowService = new ObjectWindowService();
export const actions = bindActionsTyped(ObjectWindowService, objectWindowService);
objectWindowService.actions = actions;
